import { useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * useQuickActions — Manages quick action favorites for imaging and referrals.
 * Extracted from App.js to reduce monolith.
 */
const useQuickActions = ({
  quickActions, setQuickActions,
  imagingOrders, setImagingOrders,
  specialties, setSpecialties,
}) => {
  // Persist quick actions to localStorage
  useEffect(() => {
    localStorage.setItem('writeright_quick_actions', JSON.stringify(quickActions));
  }, [quickActions]);

  const addQuickActionImaging = useCallback((type, bodyPart) => {
    if (!type) return;
    const key = `${type}|${bodyPart || ''}`;
    setQuickActions(prev => {
      if (prev.imaging.some(i => `${i.type}|${i.bodyPart || ''}` === key)) return prev;
      const newImaging = [{ type, bodyPart: bodyPart || '' }, ...prev.imaging].slice(0, 8);
      return { ...prev, imaging: newImaging };
    });
    toast.success(`Added "${type}${bodyPart ? ' - ' + bodyPart : ''}" to Quick Actions`);
  }, [setQuickActions]);

  const addQuickActionReferral = useCallback((specialty) => {
    if (!specialty) return;
    setQuickActions(prev => {
      if (prev.referrals.includes(specialty)) return prev;
      const newReferrals = [specialty, ...prev.referrals].slice(0, 8);
      return { ...prev, referrals: newReferrals };
    });
    toast.success(`Added "${specialty}" to Quick Actions`);
  }, [setQuickActions]);

  const removeQuickActionImaging = useCallback((index) => {
    setQuickActions(prev => ({
      ...prev,
      imaging: prev.imaging.filter((_, i) => i !== index)
    }));
  }, [setQuickActions]);

  const removeQuickActionReferral = useCallback((index) => {
    setQuickActions(prev => ({
      ...prev,
      referrals: prev.referrals.filter((_, i) => i !== index)
    }));
  }, [setQuickActions]);

  const applyQuickActionImaging = useCallback((img) => {
    const emptyIndex = imagingOrders.findIndex(i => !i.type);
    if (emptyIndex >= 0) {
      const updated = [...imagingOrders];
      updated[emptyIndex] = { type: img.type, bodyPart: img.bodyPart };
      setImagingOrders(updated);
    } else {
      setImagingOrders([...imagingOrders, { type: img.type, bodyPart: img.bodyPart }]);
    }
    toast.success(`Applied ${img.type}${img.bodyPart ? ' - ' + img.bodyPart : ''}`);
  }, [imagingOrders, setImagingOrders]);

  const applyQuickActionReferral = useCallback((specialty) => {
    const emptyIndex = specialties.findIndex(s => !s);
    if (emptyIndex >= 0) {
      const updated = [...specialties];
      updated[emptyIndex] = specialty;
      setSpecialties(updated);
    } else {
      setSpecialties([...specialties, specialty]);
    }
    toast.success(`Applied ${specialty} referral`);
  }, [specialties, setSpecialties]);

  return {
    addQuickActionImaging,
    addQuickActionReferral,
    removeQuickActionImaging,
    removeQuickActionReferral,
    applyQuickActionImaging,
    applyQuickActionReferral,
  };
};

export default useQuickActions;
